import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { MaterialModule } from '../material/material.module';
import { AmplifyAuthenticatorModule } from '@aws-amplify/ui-angular';
import { QuillModule } from 'ngx-quill'
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { UntypedFormBuilder, FormsModule, ReactiveFormsModule } from "@angular/forms";

import { FontAwesomeModule, FaIconComponent } from '@fortawesome/angular-fontawesome';

import { MainNavComponent } from './components/main-nav/main-nav.component';
import { SideNavComponent } from './components/side-nav/side-nav.component';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { PortalComponent } from './components/portal/portal.component';
import { PrescriptionListComponent } from "./components/prescription-list/prescription-list.component";
import { PrescriptionReviewComponent } from './components/prescription-review/prescription-review.component';
import { PrescriptionFormComponent } from "./components/prescription-form/prescription-form.component";
import { AddMedicineComponent } from "./components/add-medicine/add-medicine.component";
import { FileUploaderComponent } from "./components/file-uploader/file-uploader.component";

import { AuthGroupDirective } from './directives/auth-group.directive';

import { LocalStorageService } from './services/local-storage.service';
import { PrescriptionService } from "./services/prescription.service";
import { LoaderService } from "./services/loader.service";
import { DosageTypePackageTypeDialogComponent } from './components/dosage-type-package-type-dialog/dosage-type-package-type-dialog.component';
import { ShippingFormComponent } from "./components/shipping-form/shipping-form.component";
import { OrderListComponent } from "./components/order-list/order-list.component";
import { LoaderComponent } from './components/loader/loader.component';
import { LoaderInterceptor } from './interceptors/loader.interceptor';
import { SaveConfirmDialogComponent } from './components/save-confirm-dialog/save-confirm-dialog.component';
import { BatchListComponent } from './components/batch-list/batch-list.component';
import { BatchCreateComponent } from './components/batch-create/batch-create.component';
import { BatchViewComponent } from './components/batch-view/batch-view.component';
import { BatchOrderListComponent } from './components/batch-order-list/batch-order-list.component';
import { OrderItemListComponent } from './components/order-item-list/order-item-list.component';
import { OrderMedicineItemListComponent } from './components/order-medicine-item-list/order-medicine-item-list.component';
import { ErrorAlertComponent } from './components/error-alert/error-alert.component';
import { UtilsService } from './services/utils.service';
import { OrderSplitComponent } from './components/order-split/order-split.component';
import { UserCreateComponent } from './components/user-create/user-create.component';
import { UserListComponent } from './components/user-list/user-list.component';
import { UserViewComponent } from './components/user-view/user-view.component';
import { ConfirmBatchRequestDialogComponent } from './components/confirm-batch-request-dialog/confirm-batch-request-dialog.component';
import { AccountDetailsComponent } from './components/account-details/account-details.component';
import { UserService } from './services/user.service';
import { AuthService } from './services/auth.service';
import { ConfirmationService } from './services/confirmation.service';
import { ShippingService } from './services/shipping.service';
import { ParcelListComponent } from './components/parcel-list/parcel-list.component';
import { WaybillReviewComponent } from './components/waybill-review/waybill-review.component';
import { LinkOrderItemToBatchDialogComponent } from './components/link-order-item-to-batch-dialog/link-order-item-to-batch-dialog.component';
import { IndividualCreateComponent } from './components/individual-create/individual-create.component';
import { IndividualListComponent } from './components/individual-list/individual-list.component';
import { IndividualViewComponent } from './components/individual-view/individual-view.component';
import { IndividualService } from './services/individual.service';
import { MedicineCreateComponent } from './components/medicine-create/medicine-create.component';
import { OrderMedicineCreateComponent } from './components/order-medicine-create/order-medicine-create.component';
import { MedicineViewComponent } from './components/medicine-view/medicine-view.component';
import { MedicineListComponent } from './components/medicine-list/medicine-list.component';
import { MedicineService } from './services/medicine.service';
import { DosageUnitCreateDialogComponent } from './components/dosage-unit-create-dialog/dosage-unit-create-dialog.component';
import { DosageTypeCreateDialogComponent } from './components/dosage-type-create-dialog/dosage-type-create-dialog.component';
import { PackageTypeCreateDialogComponent } from './components/package-type-create-dialog/package-type-create-dialog.component';
import { PackageTypeSearchFieldComponent } from './components/package-type-search-field/package-type-search-field.component';
import { DosageTypeSearchFieldComponent } from './components/dosage-type-search-field/dosage-type-search-field.component';
import { DosageUnitSearchFieldComponent } from './components/dosage-unit-search-field/dosage-unit-search-field.component';
import { DosageTypeManageListComponent } from './components/dosage-type-manage-list/dosage-type-manage-list.component';
import { PackageTypeManageListComponent } from './components/package-type-manage-list/package-type-manage-list.component';
import { DosageUnitManageListComponent } from './components/dosage-unit-manage-list/dosage-unit-manage-list.component';
import { MedicineDosageTypesListComponent } from './components/medicine-dosage-types-list/medicine-dosage-types-list.component';
import { MedicineDosageTypeEditDialogComponent } from './components/medicine-dosage-type-edit-dialog/medicine-dosage-type-edit-dialog.component';
import { MedicineDosageTypeCreateDialogComponent } from './components/medicine-dosage-type-create-dialog/medicine-dosage-type-create-dialog.component';
import { MedicineDosagePackageTypeListComponent } from './components/medicine-dosage-package-type-list/medicine-dosage-package-type-list.component';
import { SageItemCodeFieldComponent } from './components/sage-item-code-field/sage-item-code-field.component';
import { MissingSageItemCodesComponent } from './components/missing-sage-item-codes/missing-sage-item-codes.component';
import { SageCustomerCodeFieldComponent } from './components/sage-customer-code-field/sage-customer-code-field.component';
import { MissingSageCustomerCodesComponent } from './components/missing-sage-customer-codes/missing-sage-customer-codes.component';
import { DirtinessService } from './services/dirtiness.service';
import { MedicineDosagePackageTypeEditDialogComponent } from './components/medicine-dosage-package-type-edit-dialog/medicine-dosage-package-type-edit-dialog.component';
import { OrderFulfilmentDialogComponent } from './components/order-fulfilment-dialog/order-fulfilment-dialog.component';
import { OrderItemFulfilmentListComponent } from './components/order-item-fulfilment-list/order-item-fulfilment-list.component';
import { BatchItemListDialogComponent } from './components/batch-item-list-dialog/batch-item-list-dialog.component';
import { CompanyService } from './services/company.service';
import { LogListComponent } from './components/log-list/log-list.component';
import { StartPageComponent } from './components/start-page/start-page.component';
import { StatisticsComponent } from './components/statistics/statistics.component';
import { OrderTrackDialogComponent } from './components/order-track-dialog/order-track-dialog.component';
import { PrescriptionUploadDialogComponent } from './components/prescription-upload-dialog/prescription-upload-dialog.component';
import { MedicineReportComponent } from './components/medicine-report/medicine-report.component';
import { BasicAuthInterceptor } from './interceptors/basic-auth.interceptor';
import { CompanyCreateComponent } from './components/company-create/company-create.component';
import { CompanyListComponent } from './components/company-list/company-list.component';
import { CompanyViewComponent } from './components/company-view/company-view.component';
import { BatchPrintDialogComponent } from './components/batch-print-dialog/batch-print-dialog.component';
import { ProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { NotFoundErrorComponent } from './components/not-found-error/not-found-error.component';
import { LogViewDialogComponent } from './components/log-view-dialog/log-view-dialog.component';
import { TermsAndConditionsComponent } from './components/terms-and-conditions/terms-and-conditions.component';
import { CompanySearchDialogComponent } from './components/company-search-dialog/company-search-dialog.component';
import { IndividualSearchDialogComponent } from './components/individual-search-dialog/individual-search-dialog.component';
import { OrderFulfilmentComponent } from './components/order-fulfilment/order-fulfilment.component';
import { AddParcelComponent } from './components/add-parcel/add-parcel.component';
import { ErrorDetailsComponent } from './components/error-details/error-details.component';
import { PrintLabelsDialogComponent } from './components/print-labels-dialog/print-labels-dialog.component';
import { BatchOrderItemListComponent } from './components/batch-order-item-list/batch-order-item-list.component';
import { OrderItemDetailsDialogComponent } from './components/order-item-details-dialog/order-item-details-dialog.component';
import { StocktakeViewComponent } from './components/stocktake-view/stocktake-view.component';
import { StocktakeReportsComponent } from './components/stocktake-reports/stocktake-reports.component';
import { StocktakeLocationLiveComponent } from './components/stocktake-location-live/stocktake-location-live.component';
import { WaybillReviewOrdersComponent } from './components/waybill-review-orders/waybill-review-orders.component';
import { AddOrderMedicineComponent } from './components/add-order-medicine/add-order-medicine.component';
import { OrderMedicineService } from './services/order-medicine.service';
import { OrderMedicineListComponent } from './components/order-medicine-list/order-medicine-list.component';

@NgModule({ declarations: [
        MainNavComponent,
        SideNavComponent,
        ConfirmDialogComponent,
        DashboardComponent,
        StartPageComponent,
        AuthGroupDirective,
        PortalComponent,
        PrescriptionListComponent,
        PrescriptionReviewComponent,
        PrescriptionFormComponent,
        AddMedicineComponent,
        DosageTypePackageTypeDialogComponent,
        ShippingFormComponent,
        OrderListComponent,
        LoaderComponent,
        FileUploaderComponent,
        SaveConfirmDialogComponent,
        BatchListComponent,
        BatchCreateComponent,
        BatchViewComponent,
        BatchOrderListComponent,
        OrderItemListComponent,
        OrderMedicineItemListComponent,
        ErrorAlertComponent,
        OrderSplitComponent,
        UserCreateComponent,
        UserListComponent,
        UserViewComponent,
        ConfirmBatchRequestDialogComponent,
        IndividualCreateComponent,
        IndividualListComponent,
        IndividualViewComponent,
        AccountDetailsComponent,
        ParcelListComponent,
        WaybillReviewComponent,
        LinkOrderItemToBatchDialogComponent,
        MedicineCreateComponent,
        OrderMedicineCreateComponent,
        MedicineListComponent,
        MedicineViewComponent,
        DosageUnitCreateDialogComponent,
        DosageTypeCreateDialogComponent,
        PackageTypeCreateDialogComponent,
        PackageTypeSearchFieldComponent,
        DosageTypeSearchFieldComponent,
        DosageUnitSearchFieldComponent,
        DosageTypeManageListComponent,
        PackageTypeManageListComponent,
        DosageUnitManageListComponent,
        MedicineDosageTypesListComponent,
        MedicineDosageTypeEditDialogComponent,
        MedicineDosageTypeCreateDialogComponent,
        MedicineDosagePackageTypeListComponent,
        MedicineDosagePackageTypeEditDialogComponent,
        SageItemCodeFieldComponent,
        SageCustomerCodeFieldComponent,
        MissingSageItemCodesComponent,
        MissingSageCustomerCodesComponent,
        OrderFulfilmentDialogComponent,
        OrderItemFulfilmentListComponent,
        BatchItemListDialogComponent,
        LogListComponent,
        StatisticsComponent,
        OrderTrackDialogComponent,
        PrescriptionUploadDialogComponent,
        MedicineReportComponent,
        CompanyCreateComponent,
        CompanyListComponent,
        CompanyViewComponent,
        BatchPrintDialogComponent,
        ProgressBarComponent,
        NotFoundErrorComponent,
        LogViewDialogComponent,
        TermsAndConditionsComponent,
        CompanySearchDialogComponent,
        IndividualSearchDialogComponent,
        OrderFulfilmentComponent,
        AddParcelComponent,
        ErrorDetailsComponent,
        PrintLabelsDialogComponent,
        BatchOrderItemListComponent,
        OrderItemDetailsDialogComponent,
        StocktakeViewComponent,
        StocktakeReportsComponent,
        StocktakeLocationLiveComponent,
        WaybillReviewOrdersComponent,
        AddOrderMedicineComponent,
        OrderMedicineListComponent
    ],
    exports: [
        MainNavComponent,
        SideNavComponent,
        AuthGroupDirective,
        FaIconComponent,
        LoaderComponent,
        AddMedicineComponent,
        AddOrderMedicineComponent,
        FileUploaderComponent,
        SaveConfirmDialogComponent,
        OrderItemListComponent,
        OrderMedicineItemListComponent,
        AccountDetailsComponent,
        OrderMedicineListComponent
    ], imports: [CommonModule,
        RouterModule,
        FontAwesomeModule,
        MaterialModule,
        AmplifyAuthenticatorModule,
        FormsModule,
        ReactiveFormsModule,
        QuillModule.forRoot(),
        NgxChartsModule], providers: [
        LocalStorageService,
        PrescriptionService,
        LoaderService,
        UntypedFormBuilder,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoaderInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: BasicAuthInterceptor,
            multi: true
        },
        UtilsService,
        UserService,
        AuthService,
        IndividualService,
        ConfirmationService,
        ShippingService,
        MedicineService,
        DirtinessService,
        CompanyService,
        OrderMedicineService,
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class CoreModule {
  constructor() {
  }
}
